import { useState, useEffect } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import { getConnectionBadgeLabel, CONNECTION_BADGE_COLORS } from './connectionBadge';

const SECTION_LABELS = {
  babysitter: 'Konekcije (Roditelj↔Dadilja)',
  family: 'Porodica',
  friend: 'Prijatelji',
  other: 'Ostalo',
};

function groupContactsByRelationship(friends) {
  const groups = { babysitter: [], family: [], friend: [], other: [] };
  for (const f of friends) {
    const t = (f.relationshipType || '').toLowerCase();
    if (t === 'babysitter' || t === 'dadilja') groups.babysitter.push(f);
    else if (t === 'family' || t === 'porodica') groups.family.push(f);
    else if (t === 'friend' || t === 'prijatelj') groups.friend.push(f);
    else groups.other.push(f);
  }
  return groups;
}

export default function NewMessageModal({
  isOpen,
  onClose,
  friends,
  existingConversations,
  getOtherUser,
  onSelectContact,
  onInviteFriend,
  t = (s) => s,
}) {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const searchLower = search.trim().toLowerCase();
  const filtered = searchLower
    ? friends.filter(
        (f) =>
          `${(f.firstName || '')} ${(f.lastName || '')}`.toLowerCase().includes(searchLower) ||
          (f.email || '').toLowerCase().includes(searchLower) ||
          (f.manualName || f.name || '').toLowerCase().includes(searchLower)
      )
    : friends;

  const groups = groupContactsByRelationship(filtered);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Nova poruka</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 focus:ring-2 focus:ring-primary-500"
            aria-label="Zatvori"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="p-2 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži kontakte..."
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
              aria-label="Pretraži kontakte"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {search ? 'Nema rezultata' : 'Nema kontakata. Dodajte prijatelje da biste mogli da šaljete poruke.'}
            </div>
          ) : (
            <>
              {(['babysitter', 'family', 'friend', 'other']).map(
                (key) =>
                  groups[key].length > 0 && (
                    <div key={key} className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-2 mb-2">
                        {SECTION_LABELS[key]}
                      </p>
                      <ul className="space-y-0.5">
                        {groups[key].map((friend) => {
                          const name = `${(friend.firstName || '')} ${(friend.lastName || '')}`.trim() || friend.manualName || friend.email || 'Nepoznat';
                          const badgeLabel = getConnectionBadgeLabel(friend.relationshipType);
                          const badgeClass = CONNECTION_BADGE_COLORS[badgeLabel] || CONNECTION_BADGE_COLORS['Ostalo'];
                          const existingConv = existingConversations.find((c) => {
                            const other = getOtherUser(c);
                            return other?.id === friend.friendUserId || other?.id === friend.id;
                          });
                          return (
                            <li key={friend.id}>
                              <button
                                type="button"
                                onClick={() => onSelectContact(friend, existingConv)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left border border-transparent focus:ring-2 focus:ring-primary-500"
                              >
                                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm overflow-hidden flex-shrink-0">
                                  {(friend.photoUrl || friend.photo_url || friend.profileImage) ? (
                                    <img src={friend.photoUrl || friend.photo_url || friend.profileImage} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    name[0]?.toUpperCase() || '?'
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{name}</p>
                                  {friend.email && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{friend.email}</p>
                                  )}
                                </div>
                                <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${badgeClass}`}>
                                  {badgeLabel}
                                </span>
                                <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Poruka</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
